<template>
  <header
    class="header"
    :class="{ 'header--authed': hasBusiness, 'header--collapsed': isCollapsed }"
  >
    <div class="header-with-logo">
      <a href="#" @click.prevent="scrollToTop" class="logo-container">
        <img :src="logo" alt="Lulu Logo" class="logo" />
      </a>
      <nav class="nav-container" ref="navContainerEl">
        <ul v-if="hasBusiness" class="nav-links" ref="navLinksEl">
          <li>
            <a
              href="#"
              @click.prevent="goToDashboard"
              :style="{ color: foregroundColor, cursor: 'pointer' }"
            >
              Insights
            </a>
          </li>
          <li>
            <a
              href="#"
              @click.prevent="goToEmployees"
              :style="{ color: foregroundColor, cursor: 'pointer' }"
            >
              Team
            </a>
          </li>
          <li>
            <a
              href="#"
              @click.prevent="goToAccount"
              :style="{ color: foregroundColor, cursor: 'pointer' }"
            >
              Account
            </a>
          </li>
        </ul>
        <ul v-else class="nav-links">
        <li>
          <a
            href="#"
            @click.prevent="openHowLuluWorks"
            :style="{ color: foregroundColor, cursor: 'pointer' }"
          >
            How Lulu works
          </a>
        </li>
        <li>
          <a
            href="#pricing-section"
            @click="scrollToPricing"
            :style="{ color: foregroundColor, cursor: 'pointer' }"
          >
            Pricing
          </a>
        </li>
        <li>
          <a
            href="#"
            @click.prevent="navigateToBusinessLookup"
            :style="{ color: foregroundColor, cursor: 'pointer' }"
          >
            Business lookup
          </a>
        </li>
        <li>
          <a
            href="#"
            @click.prevent="openTermsOfService"
            :style="{ color: foregroundColor, cursor: 'pointer' }"
          >
            Terms of service
          </a>
        </li>
        <li>
          <a
            href="#contact-section"
            @click="scrollToContact"
            :style="{ color: foregroundColor, cursor: 'pointer' }"
          >
            Contact us
          </a>
        </li>
      </ul>
      <div class="auth-actions" ref="authActionsEl">
        <button
          v-if="!authStore.user"
          @click="goToSignIn"
          class="signup-btn"
          :style="{
            color: 'white',
            backgroundColor: foregroundColor,
          }"
        >
          Sign in
        </button>
        <button
          v-if="!authStore.user"
          @click="goToSignUp"
          class="signup-btn"
          :style="{
            color: 'white',
            backgroundColor: foregroundColor,
          }"
        >
          Get started
        </button>
        <span
          v-if="hasBusiness"
          class="user-email-pill"
          :style="{ color: foregroundColor }"
          :title="authStore.user?.email"
        >
          {{ authStore.user?.email }}
        </span>
        <button
          v-if="authStore.user"
          @click="goToSignOut"
          class="signout-btn"
          :style="{
            color: foregroundColor,
            borderColor: foregroundColor,
          }"
        >
          Sign out
        </button>
      </div>
      <button
        :style="{ color: foregroundColor }"
        class="hamburger"
        @click="toggleMenu"
      >
        <span :class="{ 'is-open': menuOpen }">☰</span>
      </button>
      </nav>
    </div>
    <transition name="drawer-slide">
      <div v-if="menuOpen" class="mobile-scrim" @click.self="toggleMenu">
        <aside class="mobile-drawer">
          <div class="mobile-drawer-logo">
            <img :src="logo" alt="Lulu" />
          </div>

          <nav class="mobile-drawer-list">
            <template v-if="!hasBusiness">
              <a class="drawer-link" href="#" @click.prevent="openHowLuluWorksMobile">
                <v-icon class="drawer-link-icon">mdi-help-circle-outline</v-icon>
                <span>How Lulu works</span>
              </a>
              <a class="drawer-link" href="#pricing-section" @click="scrollToPricingMobile">
                <v-icon class="drawer-link-icon">mdi-tag-outline</v-icon>
                <span>Pricing</span>
              </a>
              <a class="drawer-link" href="#" @click.prevent="navigateToBusinessLookupMobile">
                <v-icon class="drawer-link-icon">mdi-storefront-outline</v-icon>
                <span>Business lookup</span>
              </a>
              <a class="drawer-link" href="#" @click.prevent="openTermsOfServiceMobile">
                <v-icon class="drawer-link-icon">mdi-file-document-outline</v-icon>
                <span>Terms of service</span>
              </a>
              <a class="drawer-link" href="#contact-section" @click="scrollToContactMobile">
                <v-icon class="drawer-link-icon">mdi-email-outline</v-icon>
                <span>Contact us</span>
              </a>
            </template>

            <template v-if="hasBusiness">
              <a class="drawer-link" href="#" @click.prevent="goToDashboardMobile">
                <v-icon class="drawer-link-icon">mdi-view-dashboard-outline</v-icon>
                <span>Insights</span>
              </a>
              <a class="drawer-link" href="#" @click.prevent="goToEmployeesMobile">
                <v-icon class="drawer-link-icon">mdi-account-group</v-icon>
                <span>Team</span>
              </a>
              <a class="drawer-link" href="#" @click.prevent="goToAccountMobile">
                <v-icon class="drawer-link-icon">mdi-cog-outline</v-icon>
                <span>Account</span>
              </a>
            </template>
          </nav>

          <div class="mobile-drawer-footer">
            <!-- The email pill lives in the desktop bar only, so repeat it here
                 (in full, wrapped) while the nav is collapsed. -->
            <div v-if="authStore.user" class="drawer-email">
              {{ authStore.user?.email }}
            </div>
            <a v-if="!authStore.user" class="drawer-link" href="#" @click.prevent="goToSignInMobile">
              <v-icon class="drawer-link-icon">mdi-login-variant</v-icon>
              <span>Sign in</span>
            </a>
            <a v-if="!authStore.user" class="drawer-link" href="#" @click.prevent="goToSignUpMobile">
              <v-icon class="drawer-link-icon">mdi-rocket-launch-outline</v-icon>
              <span>Get started</span>
            </a>
            <a v-if="authStore.user" class="drawer-link" href="#" @click.prevent="goToSignOutMobile">
              <v-icon class="drawer-link-icon">mdi-logout</v-icon>
              <span>Sign out</span>
            </a>
          </div>
        </aside>
      </div>
    </transition>

    <!-- How Lulu Works Modal -->
    <v-dialog v-model="showHowLuluWorks" max-width="900px" scrollable>
      <v-card>
        <v-card-title class="modal-header">
          How Lulu works
        </v-card-title>
        <v-card-text class="modal-content">
          <HowLuluWorksComponent />
        </v-card-text>
        <v-card-actions class="modal-actions">
          <v-spacer />
          <v-btn
            @click="showHowLuluWorks = false"
            class="close-modal-btn"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Terms of Service Modal -->
    <v-dialog v-model="showTermsOfService" max-width="900px" scrollable>
      <v-card>
        <v-card-title class="modal-header">
          Terms of service
        </v-card-title>
        <v-card-text class="modal-content">
          <TermsOfServiceComponent />
        </v-card-text>
        <v-card-actions class="modal-actions">
          <v-spacer />
          <v-btn
            @click="showTermsOfService = false"
            class="close-modal-btn"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </header>
</template>

<script>
import { ref, computed, nextTick, onBeforeUnmount, onMounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import HowLuluWorksComponent from "@/components/Pages/HowLuluWorksComponent.vue";
import TermsOfServiceComponent from "@/components/Pages/TermsOfServiceComponent.vue";
import { authStore, signOut } from "@/stores/authStore.js";
import { track } from "@/analytics.js";

export default {
  name: "HeaderView",
  components: {
    HowLuluWorksComponent,
    TermsOfServiceComponent,
  },
  props: {
    foregroundColor: {
      type: String,
      default: "rgb(0,109,62)",
    },
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const menuOpen = ref(false);
    const showHowLuluWorks = ref(false);
    const showTermsOfService = ref(false);
    const logo = require("@/assets/logo.png");

    // Business registration is complete once the signed-in owner has at least
    // one employer business (populated after a successful Stripe checkout).
    // Only then do we surface the authenticated dashboard nav.
    const hasBusiness = computed(() => {
      const ids = authStore.employerBusinessIds;
      return Array.isArray(ids) && ids.length > 0;
    });

    const toggleMenu = () => {
      menuOpen.value = !menuOpen.value;
    };

    // The signed-in bar carries the dashboard links plus a pill holding the
    // full email address, so how much room it needs depends on the account
    // rather than on the viewport alone. Measure the real widths and collapse
    // into the hamburger the moment the two clusters would meet, instead of
    // guessing with a fixed breakpoint. While collapsed the desktop nodes stay
    // in the DOM (out of flow, hidden) so they remain measurable.
    const navContainerEl = ref(null);
    const navLinksEl = ref(null);
    const authActionsEl = ref(null);
    const isCollapsed = ref(false);
    const COLLAPSE_GAP = 32;

    let resizeObserver = null;
    const observed = new WeakSet();

    // The signed-in nav only mounts once auth resolves, so pick up whatever
    // elements exist each time we measure rather than only on mount.
    const observeTargets = () => {
      if (!resizeObserver) return;
      [navContainerEl.value, navLinksEl.value, authActionsEl.value].forEach(
        (el) => {
          if (!el || observed.has(el)) return;
          observed.add(el);
          resizeObserver.observe(el);
        }
      );
    };

    const updateCollapsed = () => {
      observeTargets();
      if (!hasBusiness.value) {
        isCollapsed.value = false;
        return;
      }
      const container = navContainerEl.value;
      const links = navLinksEl.value;
      const actions = authActionsEl.value;
      if (!container || !links || !actions) return;
      // Below the mobile breakpoint the CSS hides both clusters outright, so
      // their widths read as 0 - leave the current value alone there.
      if (!links.offsetWidth && !actions.offsetWidth) return;
      const required = links.offsetWidth + actions.offsetWidth + COLLAPSE_GAP;
      isCollapsed.value = required > container.clientWidth;
    };

    const scheduleMeasure = () => {
      nextTick(() => updateCollapsed());
    };

    onMounted(() => {
      if (typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver(updateCollapsed);
      }
      updateCollapsed();
      window.addEventListener("resize", updateCollapsed);
      // Web fonts land after first paint and change the measured text widths.
      if (document.fonts?.ready) document.fonts.ready.then(scheduleMeasure);
    });

    onBeforeUnmount(() => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("resize", updateCollapsed);
    });

    watch([hasBusiness, () => authStore.user?.email], scheduleMeasure);

    const goToSignUp = () => {
      track("nav_clicked", { item: "get_started", source: "desktop" });
      router.push("/business-signup");
    };

    const goToSignUpMobile = () => {
      track("nav_clicked", { item: "get_started", source: "mobile_menu" });
      toggleMenu();
      router.push("/business-signup");
    };

    const goToSignIn = () => {
      track("nav_clicked", { item: "sign_in", source: "desktop" });
      router.push("/business-signin");
    };

    const goToSignInMobile = () => {
      track("nav_clicked", { item: "sign_in", source: "mobile_menu" });
      toggleMenu();
      router.push("/business-signin");
    };

    const goToDashboard = () => {
      track("nav_clicked", { item: "dashboard", source: "desktop" });
      router.push("/dashboard/insights");
    };

    const goToDashboardMobile = () => {
      track("nav_clicked", { item: "dashboard", source: "mobile_menu" });
      toggleMenu();
      router.push("/dashboard/insights");
    };

    const goToEmployees = () => {
      track("nav_clicked", { item: "employees", source: "desktop" });
      router.push("/dashboard/employees");
    };

    const goToEmployeesMobile = () => {
      track("nav_clicked", { item: "employees", source: "mobile_menu" });
      toggleMenu();
      router.push("/dashboard/employees");
    };

    const goToAccount = () => {
      track("nav_clicked", { item: "account", source: "desktop" });
      router.push("/dashboard/account");
    };

    const goToAccountMobile = () => {
      track("nav_clicked", { item: "account", source: "mobile_menu" });
      toggleMenu();
      router.push("/dashboard/account");
    };

    const goToSignOut = async () => {
      track("nav_clicked", { item: "sign_out", source: "desktop" });
      await signOut();
      router.push("/");
    };

    const goToSignOutMobile = async () => {
      track("nav_clicked", { item: "sign_out", source: "mobile_menu" });
      toggleMenu();
      await signOut();
      router.push("/");
    };

    const navigateToBusinessLookup = () => {
      track("nav_clicked", { item: "business_lookup", source: "desktop" });
      router.push('/business');
    };

    const navigateToBusinessLookupMobile = () => {
      track("nav_clicked", { item: "business_lookup", source: "mobile_menu" });
      toggleMenu();
      router.push('/business');
    };

    const scrollToPricing = (e) => {
      e.preventDefault();
      track("nav_clicked", { item: "pricing", source: "desktop" });
      if (route.path !== '/') {
        router.push('/');
        setTimeout(() => {
          const element = document.getElementById('pricing-section');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        const element = document.getElementById('pricing-section');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    };

    const scrollToPricingMobile = (e) => {
      e.preventDefault();
      track("nav_clicked", { item: "pricing", source: "mobile_menu" });
      toggleMenu();
      scrollToPricing(e);
    };

    const scrollToBusinessLookup = (e) => {
      e.preventDefault();
      if (route.path !== '/') {
        router.push('/');
        setTimeout(() => {
          const element = document.getElementById('business-lookup');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        const element = document.getElementById('business-lookup');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    };

    const scrollToBusinessLookupMobile = (e) => {
      e.preventDefault();
      toggleMenu();
      scrollToBusinessLookup(e);
    };

    const scrollToContact = (e) => {
      e.preventDefault();
      track("nav_clicked", { item: "contact", source: "desktop" });
      if (route.path !== '/') {
        router.push('/');
        setTimeout(() => {
          const element = document.getElementById('contact-section');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        const element = document.getElementById('contact-section');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    };

    const scrollToContactMobile = (e) => {
      e.preventDefault();
      track("nav_clicked", { item: "contact", source: "mobile_menu" });
      toggleMenu();
      scrollToContact(e);
    };

    const openHowLuluWorks = () => {
      track("nav_clicked", { item: "how_lulu_works", source: "desktop" });
      showHowLuluWorks.value = true;
    };

    const openHowLuluWorksMobile = () => {
      track("nav_clicked", { item: "how_lulu_works", source: "mobile_menu" });
      toggleMenu();
      showHowLuluWorks.value = true;
    };

    const openTermsOfService = () => {
      track("nav_clicked", { item: "terms_of_service", source: "desktop" });
      showTermsOfService.value = true;
    };

    const openTermsOfServiceMobile = () => {
      track("nav_clicked", { item: "terms_of_service", source: "mobile_menu" });
      toggleMenu();
      showTermsOfService.value = true;
    };

    const scrollToTop = () => {
      if (route.path !== '/') {
        router.push('/');
      }
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, route.path !== '/' ? 100 : 0);
    };

    return {
      authStore,
      hasBusiness,
      menuOpen,
      navContainerEl,
      navLinksEl,
      authActionsEl,
      isCollapsed,
      toggleMenu,
      goToSignUp,
      goToSignUpMobile,
      goToSignIn,
      goToSignInMobile,
      goToDashboard,
      goToDashboardMobile,
      goToEmployees,
      goToEmployeesMobile,
      goToAccount,
      goToAccountMobile,
      goToSignOut,
      goToSignOutMobile,
      logo,
      scrollToPricing,
      scrollToPricingMobile,
      navigateToBusinessLookup,
      navigateToBusinessLookupMobile,
      scrollToBusinessLookup,
      scrollToBusinessLookupMobile,
      scrollToContact,
      scrollToContactMobile,
      showHowLuluWorks,
      showTermsOfService,
      openHowLuluWorks,
      openHowLuluWorksMobile,
      openTermsOfService,
      openTermsOfServiceMobile,
      scrollToTop,
    };
  },
};
</script>

<style scoped>
.header {
  position: sticky;
  top: 0;
  width: 100%;
  background: #ffffff;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border-bottom: 1px solid rgba(0, 109, 62, 0.05);
  z-index: 1000;
}

.header-with-logo {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 0.75rem 2rem;
  max-width: 100%;
  width: 100%;
}

.logo-container {
  display: flex;
  align-items: center;
  margin-right: 2rem;
  text-decoration: none;
}

.logo {
  height: 50px;
  width: auto;
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.1));
  transition: transform 0.3s ease;
}

.logo:hover {
  transform: scale(1.05);
}

.nav-container {
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  /* Let the row shrink past its content so overflow is detectable rather than
     silently widening the header. */
  min-width: 0;
}

.nav-links {
  list-style: none;
  display: flex;
  gap: 2rem;
  align-items: center;
  margin: 0;
  padding: 0;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}

.auth-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-left: auto;
}

.nav-links li {
  display: flex;
  align-items: center;
  height: 100%;
}

.nav-links li a {
  text-decoration: none;
  color: #4a5568;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
}

.nav-links li a:hover {
  color: rgb(0, 109, 62);
}

.hamburger {
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.hamburger:hover {
  opacity: 0.7;
}

.signup-btn {
  padding: 8px 22px;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  text-transform: none;
  height: 100%;
  display: flex;
  align-items: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.2);
}

.signup-btn:hover {
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.3);
  transform: translateY(-1px);
}

.signin-btn {
  padding: 8px 20px;
  background: transparent;
  border: 1.5px solid rgb(0, 109, 62);
  border-radius: 999px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  text-transform: none;
  height: 100%;
  display: flex;
  align-items: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.signin-btn:hover {
  background: rgba(0, 109, 62, 0.06);
  transform: translateY(-1px);
}

/* Signed-in bar: the links sit next to the logo so the whole right-hand side
   belongs to the email pill and Sign out. */
.header--authed .nav-links {
  position: static;
  left: auto;
  transform: none;
  flex: 0 0 auto;
}

.header--authed .auth-actions {
  flex: 0 0 auto;
}

/* Collapsed by measurement: keep the desktop clusters rendered but out of flow
   so their natural widths stay readable while the hamburger is showing. */
.header--collapsed.header--authed .nav-links,
.header--collapsed.header--authed .auth-actions {
  position: absolute;
  top: 0;
  left: 0;
  visibility: hidden;
  pointer-events: none;
}

.header--collapsed.header--authed .hamburger {
  display: block;
  position: absolute;
  right: 1rem;
}

.user-email-pill {
  background: #e6f4ee;
  font-weight: 600;
  font-size: 14px;
  white-space: nowrap;
  padding: 6px 14px;
  border-radius: 999px;
  display: flex;
  align-items: center;
}

.signout-btn {
  padding: 8px 22px;
  background: transparent;
  border: 1.5px solid rgb(0, 109, 62);
  border-radius: 999px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  text-transform: none;
  height: 100%;
  display: flex;
  align-items: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.signout-btn:hover {
  background: rgba(0, 109, 62, 0.06);
  transform: translateY(-1px);
}

/* Mobile drawer - matches the dashboard navigation drawer: a right-side
   panel with a logo block on top and an icon list of links. */
.mobile-scrim {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: flex-end;
}

.mobile-drawer {
  width: 280px;
  max-width: 85%;
  height: 100%;
  background: #ffffff;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  padding: 8px 12px;
  overflow-y: auto;
}

.mobile-drawer-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px 12px 16px;
}

.mobile-drawer-logo img {
  height: 56px;
  object-fit: contain;
}

.mobile-drawer-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.mobile-drawer-footer {
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 12px;
}

.drawer-link {
  display: flex;
  align-items: center;
  gap: 24px;
  min-height: 52px;
  padding: 0 16px;
  border-radius: 8px;
  text-decoration: none;
  color: #1a202c;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
}

.drawer-email {
  padding: 8px 16px 12px;
  font-size: 13px;
  font-weight: 600;
  color: rgb(0, 109, 62);
  word-break: break-all;
}

.drawer-link-icon {
  color: #5f6b7a;
  font-size: 24px;
  transition: color 0.2s ease;
}

.drawer-link:hover,
.drawer-link:active {
  background: #e6f4ee;
  color: rgb(0, 109, 62);
}

.drawer-link:hover .drawer-link-icon,
.drawer-link:active .drawer-link-icon {
  color: rgb(0, 109, 62);
}

/* Drawer slide-in from the right */
.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: opacity 0.25s ease;
}

.drawer-slide-enter-active .mobile-drawer,
.drawer-slide-leave-active .mobile-drawer {
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.drawer-slide-enter-from,
.drawer-slide-leave-to {
  opacity: 0;
}

.drawer-slide-enter-from .mobile-drawer,
.drawer-slide-leave-to .mobile-drawer {
  transform: translateX(100%);
}

/* Responsive Styles */
@media (max-width: 1024px) {
  .nav-links,
  .auth-actions {
    display: none;
  }
  .hamburger {
    display: block;
    position: absolute;
    right: 1rem;
  }
}

/* Wider than that the authenticated bar collapses on measurement instead of a
   fixed breakpoint - see updateCollapsed(). */

/* Modal Styles */
.modal-header {
  background: linear-gradient(135deg, rgb(0, 109, 62) 0%, rgb(0, 85, 48) 100%) !important;
  color: white !important;
  font-weight: 700 !important;
  font-size: 20px !important;
  padding: 20px !important;
  text-align: center !important;
}

.modal-content {
  padding: 24px !important;
  max-height: 500px;
  overflow-y: auto;
}

.modal-content :deep(.v-img) {
  display: none !important;
}

.modal-content :deep(.text-container) {
  display: none !important;
}

.modal-content :deep(.content) {
  max-width: 100% !important;
  padding: 0 !important;
}

.modal-actions {
  padding: 16px 24px !important;
  background-color: #f5f7fa;
  border-top: 1px solid #e2e8f0;
}

.close-modal-btn {
  background: rgb(0, 109, 62) !important;
  color: white !important;
  text-transform: none !important;
  font-weight: 600 !important;
  border-radius: 999px !important;
  padding: 8px 24px !important;
}

.close-modal-btn:hover {
  box-shadow: 0 4px 12px rgba(0, 109, 62, 0.3) !important;
}
</style>
